import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import { taskSetService } from '../services/taskSetService';
import { jobService } from '../services/jobService';
import { taskService } from '../services/taskService';

const emptySet = () => ({ name: '', items: [{ title: '' }] });

const TaskSetModal = ({ taskSet, onSave, onClose }) => {
  const [name, setName] = useState(taskSet?.name || '');
  const [items, setItems] = useState(
    taskSet?.items?.length > 0 ? taskSet.items.map((i) => ({ title: i.title })) : [{ title: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const importFileRef = useRef(null);

  const handleItemChange = (index, value) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { title: value } : item)));
  };

  const addItem = () => setItems((prev) => [...prev, { title: '' }]);

  const importFromFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = (ev.target.result || '')
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length > 0) {
        setItems((prev) => {
          const existing = prev.filter((i) => i.title.trim());
          return [...existing, ...lines.map((l) => ({ title: l }))];
        });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (index === items.length - 1) {
        addItem();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const filledItems = items.filter((i) => i.title.trim());
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ name: name.trim(), items: filledItems });
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {taskSet ? 'Edit Task Set' : 'New Task Set'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Set Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. New Site Installation, Quarterly Service"
                autoFocus
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tasks</label>
                <span className="text-xs text-gray-400 dark:text-gray-500">Press Enter to add another</span>
              </div>
              <div className="space-y-2">
                {items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 dark:text-gray-500 w-5 text-right shrink-0">{index + 1}.</span>
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => handleItemChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      placeholder={`Task ${index + 1}`}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                      className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-30 transition-colors"
                      title="Remove task"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addItem}
                className="mt-2 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
              >
                + Add Task
              </button>
              <input
                ref={importFileRef}
                type="file"
                accept=".txt,text/plain"
                className="hidden"
                onChange={importFromFile}
              />
              <button
                type="button"
                onClick={() => importFileRef.current?.click()}
                className="mt-2 ml-3 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                ↑ Import .txt
              </button>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : taskSet ? 'Save Changes' : 'Create Set'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TaskSets = () => {
  const [taskSets, setTaskSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedSetId, setExpandedSetId] = useState(null);
  const [copyToJobSet, setCopyToJobSet] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [copyToJobId, setCopyToJobId] = useState('');
  const [copyingToJob, setCopyingToJob] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);

  const { user, logout } = useAuth();

  const fetchTaskSets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await taskSetService.getTaskSets();
      setTaskSets(data.data || []);
      setError('');
    } catch {
      setError('Failed to load task sets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTaskSets();
  }, [fetchTaskSets]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSave = async (formData) => {
    if (editingSet) {
      await taskSetService.updateTaskSet(editingSet.id, formData);
      showSuccess('Task set updated!');
    } else {
      await taskSetService.createTaskSet(formData);
      showSuccess('Task set created!');
    }
    await fetchTaskSets();
    setShowModal(false);
    setEditingSet(null);
  };

  const handleDelete = async (id) => {
    try {
      await taskSetService.deleteTaskSet(id);
      setTaskSets((prev) => prev.filter((s) => s.id !== id));
      setDeleteConfirm(null);
      showSuccess('Task set deleted.');
    } catch {
      setError('Failed to delete task set.');
    }
  };

  const openEdit = (set) => {
    setEditingSet(set);
    setShowModal(true);
  };

  const openCopyToJob = async (set) => {
    setCopyToJobSet(set);
    setCopyToJobId('');
    if (jobs.length === 0) {
      setJobsLoading(true);
      try {
        const res = await jobService.getJobs();
        setJobs(res.data || []);
      } catch { /* ignore */ } finally {
        setJobsLoading(false);
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSet(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-2.5 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">Task Sets</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <ThemeToggle />
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base order-last sm:order-none">{user?.name}</span>
              <button
                onClick={logout}
                className="bg-red-500 text-white border-0 rounded-md px-3 sm:px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-2.5 py-8">
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-green-700 dark:text-green-300 text-sm font-medium">
            ✓ {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Task Sets</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              Reusable collections of tasks you can reference for any job.
            </p>
          </div>
          <button
            onClick={() => { setEditingSet(null); setShowModal(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-md whitespace-nowrap"
          >
            <span className="text-lg leading-none">+</span>
            New Task Set
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
              <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
              Loading task sets…
            </div>
          </div>
        ) : taskSets.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center py-20 text-center">
            <div className="text-5xl mb-4">📋</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No task sets yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm">
              Create reusable task collections — like "New Site Installation" — to stay consistent across jobs.
            </p>
            <button
              onClick={() => { setEditingSet(null); setShowModal(true); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
            >
              <span className="text-lg leading-none">+</span>
              New Task Set
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {taskSets.map((set) => {
              const isExpanded = expandedSetId === set.id;
              return (
                <div key={set.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between px-6 py-4">
                    <button
                      className="flex items-center gap-3 text-left flex-1 min-w-0"
                      onClick={() => setExpandedSetId(isExpanded ? null : set.id)}
                    >
                      <span
                        className={`text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                        style={{ fontSize: '10px' }}
                      >
                        ▶
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">{set.name}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {set.items?.length || 0} {set.items?.length === 1 ? 'task' : 'tasks'}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      <button
                        onClick={() => openCopyToJob(set)}
                        className="px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-colors font-medium"
                      >
                        Copy to Job
                      </button>
                      <button
                        onClick={() => openEdit(set)}
                        className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                      >
                        Edit
                      </button>
                      {deleteConfirm === set.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-600 dark:text-red-400">Delete?</span>
                          <button
                            onClick={() => handleDelete(set.id)}
                            className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors"
                          >Yes</button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                          >No</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(set.id)}
                          className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 dark:border-gray-700 px-6 py-4">
                      {!set.items?.length ? (
                        <p className="text-sm text-gray-400 dark:text-gray-500 italic">No tasks in this set.</p>
                      ) : (
                        <ol className="space-y-2">
                          {set.items.map((item, index) => (
                            <li key={item.id} className="flex items-center gap-3 text-sm">
                              <span className="text-xs text-gray-400 dark:text-gray-500 w-5 text-right shrink-0">{index + 1}.</span>
                              <span className="text-gray-700 dark:text-gray-300">{item.title}</span>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {showModal && (
        <TaskSetModal
          taskSet={editingSet}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}

      {copyToJobSet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCopyToJobSet(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Copy to Job</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Add all {copyToJobSet.items?.length || 0} tasks from <span className="font-medium text-gray-700 dark:text-gray-300">{copyToJobSet.name}</span> to a job.
            </p>
            {jobsLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading jobs…</p>
            ) : (
              <select
                value={copyToJobId}
                onChange={(e) => setCopyToJobId(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              >
                <option value="">-- Select a job --</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>{job.title}{job.site?.name ? ` — ${job.site.name}` : ''}</option>
                ))}
              </select>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCopyToJobSet(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >Cancel</button>
              <button
                disabled={!copyToJobId || copyingToJob}
                onClick={async () => {
                  if (!copyToJobId || copyingToJob) return;
                  setCopyingToJob(true);
                  try {
                    for (const item of copyToJobSet.items || []) {
                      await taskService.createTask({ job_id: copyToJobId, title: item.title });
                    }
                    showSuccess(`Copied ${copyToJobSet.items?.length || 0} tasks to job.`);
                    setCopyToJobSet(null);
                  } catch { /* ignore */ } finally {
                    setCopyingToJob(false);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >{copyingToJob ? 'Copying…' : 'Copy Tasks'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskSets;
